"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { PriorityPill, PRIORITY_COLORS } from "@/components/ui/PriorityPill";
import { SegnoFascia } from "@/components/ui/SegnoFascia";
import { ENTITY_COLORS, ENTITY_SYMBOLS, PRIORITY_MAP, TIPO_DERIVATO } from "@/lib/constants";
import type { NewsItem, Priority } from "@/lib/types";
import {
  binarioRullo,
  fmtDispatchId,
  truncate,
  type PassoRullo,
} from "@/lib/format";
import { articleHref } from "@/lib/slug";

/**
 * I tipi che nel rullo si presentano come **pezzi elaborati** e non come
 * notizie: il dossier e l'editoriale.
 *
 * Non è una distinzione di gusto. Gli altri tipi — focus, nuovi scenari,
 * segnali deboli, ibrido, teatri di crisi, notizie, pmi, articoli,
 * archivio — sono dispacci, cioè la cronaca del rullo: escono a ritmo
 * quotidiano, si leggono in fila e la loro forma è la riga. Report ed
 * editoriale sono lavoro della casa, firmato e costruito, e nel resto
 * del sito hanno già una grafica loro (la card di /report, la lastra di
 * /editoriali). Scorrendo il rullo devono distinguersi, altrimenti un
 * dossier su cui si è lavorato un mese si presenta come la notizia
 * delle undici.
 *
 * Restano **righe**, però: niente card, niente griglia. Quello che
 * cambia è il peso — velo di fondo nel tono, filetto orizzontale in
 * testa, marchio in apertura della riga meta — che è la lingua che
 * /editoriali usa per le sue righe e /report per le sue card. Il tono
 * non compare mai come barra laterale: quella è uscita, e il perché sta
 * nel commento sull'<article> qui sotto.
 *
 * Perché un insieme qui e non in `lib/constants.ts`: è una scelta di
 * resa di questo componente, non un filtro che qualcuno mandi a monte,
 * e nessun Server Component la legge. Se un giorno servisse anche
 * altrove va spostata in `lib/`, perché una `export const` di un modulo
 * «use client» letta dal server è il riferimento al modulo e non il suo
 * valore (è la trappola di `LEAD_DISPACCI`).
 */
const TIPI_ELABORATI: ReadonlySet<Priority> = new Set<Priority>([
  "report",
  "editoriale",
]);

type DispatchRowProps = {
  item: NewsItem;
  isNew?: boolean;
  /** Ogni quanto la lista mette uno stacco, e quindi che cosa scrive il
   *  binario a sinistra: l'ora se la lista è spezzata per giorno, la
   *  data se è spezzata per mese. Lo decide chi rende la lista, con
   *  `passoRullo` — il perché sta lì. */
  passo?: PassoRullo;
  /** Termine di ricerca: se presente, le occorrenze nel titolo e nel
   *  lead vengono evidenziate. */
  highlight?: string;
};

function escapeRegExpStr(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Spezza un testo nelle parti che combaciano con `q` (case-insensitive)
 *  e restituisce un array di parti, marcate per essere "highlight". */
function splitByQuery(text: string, q: string) {
  if (!q) return [{ text, hit: false }];
  const parts = text.split(new RegExp(`(${escapeRegExpStr(q)})`, "gi"));
  const lower = q.toLowerCase();
  return parts.map((part) => ({ text: part, hit: part.toLowerCase() === lower }));
}

/**
 * La grammatica delle voci che portano da qualche parte, una sola per
 * tutta la riga: un filetto sotto il testo, nel tono della voce, al 35%
 * a riposo e pieno sotto il dito.
 *
 * Nasce nella scheda dei riferimenti, dove serviva a dire dove comincia
 * e dove finisce ogni nome ("Asia occidentale" è una voce sola, non
 * due), e vale identica nella riga meta: paese, rubrica e tipo sono
 * anche loro tre porte verso il rullo filtrato, e prima si annunciavano
 * ognuna a modo suo — una cambiava colore al passaggio, le altre due
 * non facevano niente pur essendo diventate cliccabili.
 *
 * Il tono sta in `--ent-tone` e lo smorzato in `--ent-soft`: un
 * `color-mix` e non l'esadecimale con il suffisso di opacità, perché
 * qui i toni sono variabili (`var(--p-place)`, `var(--tono)`) e
 * `var(--p-report)66` non è un colore.
 */
function toniVoce(tone: string): React.CSSProperties {
  return {
    ["--ent-tone" as string]: tone,
    ["--ent-soft" as string]: `color-mix(in srgb, ${tone} 35%, transparent)`,
  } as React.CSSProperties;
}

/** Il filetto vero e proprio. Senza il rientro sotto il testo: quello
 *  lo aggiunge chi la usa, perché nella riga meta e nella scheda dei
 *  riferimenti costa due cose diverse (vedi sotto), e due `pb-[Npx]`
 *  nella stessa stringa di classi sono una scommessa: sono utility di
 *  pari rango, e a decidere è l'ordine in cui Tailwind le emette, non
 *  quello in cui sono scritte. */
const VOCE_CLICCABILE =
  "cursor-pointer border-b border-b-transparent bg-transparent p-0 transition-colors hover:border-b-[color:var(--ent-tone)]";

/** Il rientro del filetto nella riga meta: scende di tre pixel sotto il
 *  testo ma resta fuori dall'altezza della riga, che è `items-center`:
 *  un rientro contato nel flusso alzerebbe le tre voci rispetto alla
 *  coda a destra (quando, codice, riferimenti), che rientro non ne ha.
 *  Il margine è quattro e non tre perché il bordo entra nel conto: la
 *  scatola cresce di 3 + 1 e sono quelli da restituire. Misurato: con
 *  −3 la riga meta passava da 18 a 19 pixel e le voci stavano mezzo
 *  pixel più in alto della coda, con −4 le due misure coincidono. */
const VOCE_FILETTO = "pb-[3px] -mb-[4px]";

type RefItem = { name: string; href?: string };
type RefGroup = {
  key: string;
  label: string;
  symbol: string;
  tone: string;
  items: RefItem[];
};

/**
 * I riferimenti del dispaccio: un pannello ancorato al comando che li
 * apre, e non un blocco dentro la riga.
 *
 * Tre forme sono state provate dentro la riga e nessuna regge, sempre
 * per la stessa ragione: un elenco di nomi raggruppati per tipo e una
 * lista che si scorre sono due mestieri diversi, e messi nella stessa
 * casella si rovinano a vicenda. Aggiunto sotto il dispaccio, il blocco
 * lo raddoppiava (riga da 143 a 422 pixel) e con due aperti la pagina
 * diventava quattro blocchi dello stesso peso in fila; al posto del
 * sommario, la riga restava della sua misura ma perdeva la frase per
 * cui un elenco esiste; in fila piatta, i nomi non si capiva più che
 * cosa fossero.
 *
 * Fuori dal flusso non c'è niente da conciliare. La riga non cambia di
 * un pixel quando si apre, il sommario resta dov'è, la pagina non si
 * riassesta sotto le dita e — soprattutto — due pannelli aperti insieme
 * non esistono: il primo si chiude al tocco fuori, che è lo stesso
 * tocco che apre il secondo.
 *
 * Non è una forma nuova per il progetto: è quella dei menu Paesi e
 * Categorie della barra qui sopra (`FilterSelect`) e, sul telefono, del
 * pannello dei filtri (<PannelloRicerca>). Sotto `md` il pannello è un
 * foglio appoggiato in basso con il velo dietro, perché 420 pixel su
 * uno schermo da 390 non sono un pannello ancorato, sono la pagina.
 *
 * I gruppi stanno in colonna: qui la larghezza è 420 e non i 1.326 della
 * riga, quindi la griglia a più colonne che serviva là dentro non ha
 * più niente da riempire.
 */
function RefPanel({
  groups,
  total,
  verso,
  onPick,
  onClose,
}: {
  groups: RefGroup[];
  total: number;
  /** Da che parte si apre rispetto al comando. Lo decide la riga, che
   *  al momento del tocco sa quanto schermo ha sotto. */
  verso: "giu" | "su";
  onPick: (href: string) => void;
  onClose: () => void;
}) {
  return (
    <>
      {/* Il velo, solo sul telefono: là il pannello è un foglio e serve
          dire che la pagina sotto è sospesa. Da `md` in su il pannello
          è ancorato al comando e la pagina resta viva: un velo la
          spegnerebbe per un elenco di otto nomi. */}
      <span
        aria-hidden
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
        className="fixed inset-0 z-[880] bg-ink/40 md:hidden"
      />
      <div
        role="group"
        aria-label="Riferimenti del dispaccio"
        onClick={(e) => e.stopPropagation()}
        /* `z-[5]` da `md` in su e non `z-50`: sopra le righe, che non
           hanno z-index, e SOTTO la barra dei filtri appesa in cima
           (z-10) e la barra di ricerca (z-15). Un pannello che scorre
           e passa sopra la barra appesa si legge come un errore.
           Sul telefono invece va sopra tutto, come il foglio dei
           filtri: 900.
           `max-h` e scorrimento proprio: un dispaccio d'archivio può
           portare venti paesi, e il pannello non deve uscire dallo
           schermo. */
        className={`oi-reveal max-md:fixed max-md:inset-x-3 max-md:bottom-3 max-md:z-[900] max-md:max-h-[72svh] max-md:overflow-y-auto md:absolute md:right-0 md:z-[5] md:max-h-[70svh] md:w-[420px] md:overflow-y-auto rounded-sm border border-plateRule bg-surface p-[calc(var(--oi-card-pad)-1px)] text-left shadow-[0_18px_40px_-18px_rgba(0,0,0,.45)] ${
          verso === "su" ? "md:bottom-full md:mb-2" : "md:top-full md:mt-2"
        }`}
      >
        {/* La testata del pannello. Dice la parola una volta sola, e
            qui ha senso: il pannello galleggia sopra la pagina e deve
            dire di che cosa parla. Dentro la riga era la stessa frase
            ripetuta su ogni dispaccio aperto. */}
        <div className="mb-heading flex items-baseline gap-3 text-nota uppercase tracking-etichetta text-muted">
          <span className="flex-1 font-bold text-ink">Riferimenti</span>
          <span className="font-mono tabular-nums text-muted">{total}</span>
          {/* La chiusura c'è solo sul telefono: là il pannello copre la
              pagina e il comando che l'ha aperto non si vede più. Sul
              sito il comando è a due centimetri, in fondo alla riga
              meta, e porta già il meno. */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            aria-label="Chiudi i riferimenti"
            className="-my-2 -mr-2 ml-1 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-sm bg-transparent p-0 text-meta leading-none text-muted transition-colors hover:bg-bg hover:text-accent md:hidden"
          >
            ✕
          </button>
        </div>
      <dl className="m-0 grid gap-y-heading">
        {groups.map((g, i) => (
          /* Un `<div>` per gruppo dentro la `<dl>`: è la forma con cui
             l'HTML raggruppa una coppia dt/dd, e serve perché le due
             parti stanno incolonnate e devono viaggiare insieme nella
             cella della griglia. */
          <div
            key={g.key}
            className="oi-reveal m-0"
            style={{ animationDelay: `${60 + i * 60}ms` }}
          >
            {/* L'etichetta del gruppo: simbolo del tipo nel suo tono,
                nome in nero, filetto nel tono, conteggio in mono e muto
                in coda. È la grammatica degli occhielli di prima pagina
                (10px, 0,18em, peso 700, filetto, numero), un gradino più
                in basso. Prima il conteggio stava incolonnato a 235 per
                tutti i gruppi, cioè a un centinaio di pixel dalla
                parola: adesso ce lo porta il filetto. */}
            <dt className="m-0 flex items-baseline gap-2.5 text-nota uppercase tracking-etichetta text-muted">
              <span
                aria-hidden
                className="leading-none"
                style={{ color: g.tone }}
              >
                {g.symbol}
              </span>
              <span className="flex-1 truncate font-bold text-ink">
                {g.label}
              </span>
              <span className="font-mono tabular-nums text-muted">
                {g.items.length}
              </span>
            </dt>

            {/* Otto pixel sotto l'etichetta, non sedici: i nomi sono la
                riga che l'etichetta annuncia, e con sedici il gruppo si
                sarebbe staccato da sé stesso quanto dal gruppo accanto.

                I nomi stanno a `text-corpo`, lo stesso corpo del
                sommario di cui prendono il posto: la casella non cambia
                peso passando dall'uno agli altri. */}
            <dd className="m-0 mt-1.5 flex flex-col items-start gap-y-px text-corpo leading-[1.5]">
              {g.items.map((it) =>
                it.href ? (
                  <button
                    key={it.name}
                    type="button"
                    // Sta dentro la riga: senza fermare l'evento il
                    // click arriverebbe al comando che l'ha aperta.
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onPick(it.href!);
                    }}
                    style={{ ["--ent-tone" as string]: g.tone } as React.CSSProperties}
                    className="cursor-pointer bg-transparent p-0 text-left font-medium text-ink transition-colors hover:[color:var(--ent-tone)]"
                  >
                    {it.name}
                  </button>
                ) : (
                  // I temi non si cliccano: restano più chiari e non si
                  // accendono al passaggio. Prima lo diceva una
                  // sottolineatura tratteggiata, cioè una linea in più
                  // per ogni voce.
                  <span key={it.name} className="font-normal text-ink/55">
                    {it.name}
                  </span>
                )
              )}
            </dd>
          </div>
        ))}
      </dl>

        {/* L'istruzione, una volta sola e in fondo: qui non annuncia
            niente, spiega che cosa succede toccando un nome. Dentro la
            riga stava in testa e si rileggeva a ogni dispaccio aperto. */}
        <p className="m-0 mt-band text-nota uppercase leading-[1.5] tracking-etichetta text-muted">
          Cliccane uno per vedere gli altri dispacci che lo citano
        </p>
      </div>
    </>
  );
}

/** Dispatch row — mirrors the search result layout: meta row with
 *  ID + PriorityPill + category chip + date/relTime, then title, accent
 *  dash, lead with left accent border, and entity chips. */
export function DispatchRow({
  item,
  isNew = false,
  passo = "giorno",
  highlight,
}: DispatchRowProps) {
  const router = useRouter();
  const short = truncate(item.lead, 420);
  /** Dossier o editoriale: la riga prende il trattamento marcato. */
  const elaborato = TIPI_ELABORATI.has(item.priority);
  // La rubrica è la stringa vuota su quasi tutto l'archivio: 101.791
  // dispacci su 103.000 non ne hanno una, e a monte è `""` e non
  // `null`, quindi nessun `?? "…"` scatta mai. Va saltata per intero,
  // anello compreso: una voce vuota lasciava in riga il suo segno e uno
  // dei vuoti del `gap-3`, cioè un puntino sospeso fra il paese e il
  // tipo che non annunciava niente.
  const rubrica = item.category.trim();
  const places = item.entities.places || [];
  const companies = item.entities.companies || [];
  const institutions = item.entities.institutions || [];
  const persons = item.entities.persons || [];
  const tags = item.tags || [];

  // Il primo paese è già accanto all'ID del dispaccio: qui sotto vanno
  // solo gli altri.
  const otherPlaces = places.slice(1);

  // Quanti riferimenti ci sono in tutto dietro il pulsante. Conta le
  // entità vere, non i chip visibili: quelli si fermano a cinque per
  // tipo e il resto sta dentro i "+N".
  const refCount =
    otherPlaces.length +
    companies.length +
    institutions.length +
    persons.length +
    tags.length;

  /** I riferimenti divisi per tipo. Prima chi agisce: persone, aziende,
   *  istituzioni. I paesi vengono dopo, perché sono la lista più lunga e
   *  la più generica — "Asia", "Asia occidentale", "Austria" — e in
   *  testa alla scheda coprivano i nomi che dicono davvero qualcosa. I
   *  temi chiudono: non sono entità riconosciute nel testo ma etichette
   *  messe a monte. Ogni gruppo porta la sua etichetta: senza, i nomi
   *  finivano tutti in fila senza che si capisse cosa fossero.
   *
   *  I filtri puntano a `/rullo`, non a `/`. La prima pagina legge
   *  `?place=` e `?etype=` e fa perfino la fetch filtrata, ma in
   *  `homeMode` `FeedClient` non rende mai la lista del rullo: al suo
   *  posto c'è la lastra dell'editoriale. Cliccando un paese si tornava
   *  quindi in prima pagina identica a prima, con il filtro
   *  nell'indirizzo e nessun risultato a schermo. `/rullo` accetta gli
   *  stessi parametri e la lista la disegna davvero. */
  const refGroups: RefGroup[] = [];
  if (persons.length) {
    refGroups.push({
      key: "persons",
      label: "Persone",
      symbol: ENTITY_SYMBOLS.persons,
      tone: ENTITY_COLORS.persons,
      items: persons.map((n) => ({
        name: n,
        href: `/rullo?etype=persons&entity=${encodeURIComponent(n)}`,
      })),
    });
  }
  if (companies.length) {
    refGroups.push({
      key: "companies",
      label: "Aziende",
      symbol: ENTITY_SYMBOLS.companies,
      tone: ENTITY_COLORS.companies,
      items: companies.map((n) => ({
        name: n,
        href: `/rullo?etype=companies&entity=${encodeURIComponent(n)}`,
      })),
    });
  }
  if (institutions.length) {
    refGroups.push({
      key: "institutions",
      label: "Istituzioni",
      symbol: ENTITY_SYMBOLS.institutions,
      tone: ENTITY_COLORS.institutions,
      items: institutions.map((n) => ({
        name: n,
        href: `/rullo?etype=institutions&entity=${encodeURIComponent(n)}`,
      })),
    });
  }
  if (otherPlaces.length) {
    refGroups.push({
      key: "places",
      label: "Paesi",
      symbol: ENTITY_SYMBOLS.places,
      tone: ENTITY_COLORS.places,
      items: otherPlaces.map((n) => ({
        name: n,
        href: `/rullo?place=${encodeURIComponent(n)}`,
      })),
    });
  }
  if (tags.length) {
    // I tag non sono entità riconosciute nel testo ma etichette messe
    // a monte, e non hanno una rotta di filtro: restano da leggere.
    refGroups.push({
      key: "tags",
      label: "Temi",
      symbol: ENTITY_SYMBOLS.sectors,
      tone: ENTITY_COLORS.sectors,
      items: tags.map((n) => ({ name: n })),
    });
  }

  // Chiusi di default. Un dispaccio che si porta dietro venti fra
  // "Asia", "Asia occidentale", "Austria" e sigle varie sotto il lead
  // faceva più rumore della notizia: la riga sembrava l'output di un
  // estrattore, non un dispaccio. Chi li vuole li apre.
  const [refsOpen, setRefsOpen] = useState(false);
  /** La scatola che tiene comando e pannello. Serve a distinguere il
   *  "dentro" dal "fuori" nella chiusura qui sotto, e a misurare quanto
   *  schermo resta sotto il comando. */
  const ancora = useRef<HTMLSpanElement | null>(null);
  /** Da che parte si apre il pannello. */
  const [verso, setVerso] = useState<"giu" | "su">("giu");

  /** Il verso si decide nel momento del tocco, non nel render: un
   *  pannello che si apre verso il basso su una riga che sta in fondo
   *  allo schermo nasce sotto la piega, e per leggerlo bisogna
   *  scorrere — cioè si apre una cosa che non si vede. Se sotto non c'è
   *  abbastanza spazio e sopra ce n'è di più, si apre in su.
   *
   *  La soglia è 360 pixel e non l'altezza vera del pannello: quella si
   *  conoscerebbe solo dopo averlo montato, e misurarla lì vorrebbe
   *  dire vederlo saltare da una parte all'altra. 360 è la misura dei
   *  pannelli veri (da 207 a 430 sui dispacci del rullo), presa per
   *  eccesso.
   *
   *  Sotto `md` non conta: là il pannello è un foglio appoggiato in
   *  basso e non è ancorato a niente. */
  const commuta = () => {
    if (!refsOpen) {
      const r = ancora.current?.getBoundingClientRect();
      const sotto = r ? window.innerHeight - r.bottom : 0;
      const sopra = r ? r.top : 0;
      setVerso(sotto < 360 && sopra > sotto ? "su" : "giu");
    }
    setRefsOpen((v) => !v);
  };

  /** Il pannello si chiude al tocco fuori e con Esc.
   *
   *  Il tocco fuori è anche il modo in cui si ottiene, gratis, che di
   *  pannelli aperti ce ne sia sempre e solo uno: si ascolta
   *  `pointerdown`, che arriva prima del `click`, quindi premendo il
   *  comando di un altro dispaccio il primo pannello si chiude e subito
   *  dopo il secondo si apre. Con `click` i due eventi arriverebbero
   *  nell'ordine sbagliato e il secondo pannello si aprirebbe e
   *  richiuderebbe nello stesso gesto.
   *
   *  Niente chiusura sullo scorrimento: il pannello è ancorato alla sua
   *  riga e scorre con lei, quindi non si scolla; chiuderlo mentre si
   *  scorre sarebbe solo un modo per perderlo. */
  useEffect(() => {
    if (!refsOpen) return;
    const fuori = (e: PointerEvent) => {
      if (!ancora.current?.contains(e.target as Node)) setRefsOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setRefsOpen(false);
    };
    document.addEventListener("pointerdown", fuori);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", fuori);
      document.removeEventListener("keydown", esc);
    };
  }, [refsOpen]);

  return (
    // La riga NON è più un link. Prima lo era tutta, e bastava un tocco
    // qualsiasi — di fianco a un nome, sotto la data, nello spazio vuoto
    // a destra — per ritrovarsi dentro l'articolo. Ad aprire il
    // dispaccio è solo il suo titolo; il paese, i riferimenti e i nomi
    // dentro la scheda restano comandi loro.
    <article
      // **Niente filetto fra una riga e l'altra.** Ce n'è stato uno per
      // riga, e su una pagina da cento dispacci erano cento linee
      // orizzontali: divisione e basta, non dicevano niente. Una linea
      // in un elenco serve quando il vuoto non basta a dire dove
      // finisce un elemento e comincia il successivo; qui il vuoto
      // basta, perché ogni riga si apre con l'ora sul suo binario e con
      // un titolo in grassetto, che sono due segni d'attacco molto più
      // forti di un filo grigio.
      //
      // Le linee che restano in pagina sono tre, e ognuna significa
      // qualcosa: il bordo sotto la riga di ricerca (finisce
      // l'apparato, comincia il giornale), il filetto dello stacco di
      // giornata (cambia il giorno) e quello sopra la paginazione
      // (finisce la lista). Erano centotré.
      //
      // **Niente barra verticale sul fianco.** C'è stata, tre pixel nel
      // tono del tipo lungo tutto il bordo sinistro, e non torna: una
      // striscia colorata per riga, ripetuta cento volte in colonna,
      // non si legge come il tono di quel dispaccio ma come un bordo
      // dell'elenco, e in mezzo alla pagina faceva una seconda colonna
      // di colore che non corrispondeva a niente. Il tono del tipo non
      // si perde: sta nella targa, e sui pezzi elaborati anche nel velo
      // di fondo e nel filetto orizzontale in testa alla riga — cioè
      // dove /editoriali lo mette, mai di fianco.
      //
      // Il margine torna quindi tondo, `mx-4 sm:mx-7`: prima erano 13 e
      // 25 più i tre pixel del bordo, che sommati davano gli stessi 16
      // e 28. Il testo non si sposta di un pixel.
      className={`mx-4 sm:mx-7 ${
        isNew ? "animate-[oi-slideIn_600ms_ease-out]" : ""
      }`}
      style={
          {
            // Il tono del tipo, scritto una volta sola e letto dalle
            // classi (`text-[var(--tono)]`, `bg-[var(--tono-velo)]`).
            //
            // In una custom property e non in un `backgroundColor`
            // inline: uno `style` inline vince su qualunque classe, e
            // qui sopra il velo passano anche gli stati (il puntatore
            // sulle voci, il fuoco da tastiera). È la stessa forma di
            // <Riquadro> in /editoriali.
            //
            // E una variabile e non un esadecimale: i `--p-*` sono
            // dichiarati due volte in globals.css, sotto `:root` e
            // sotto `.dark`, con i contrasti misurati uno per uno. Un
            // hex scritto qui resterebbe il valore del tema chiaro e
            // sul fondo scuro sparirebbe (focus dava 1,73:1, navy su
            // navy). Il ripiego è l'accent del sito, che la coppia ce
            // l'ha anche lui.
            ["--tono" as string]:
              PRIORITY_COLORS[item.priority]?.fg ?? "var(--color-accent)",
            // Il velo di fondo dei pezzi elaborati. `color-mix` e non
            // il suffisso di opacità sull'esadecimale: `var(--p-report)12`
            // non è un colore. La quota è misurata, e il commento che
            // la spiega sta sul velo, più sotto.
            ["--tono-velo" as string]:
              `color-mix(in srgb, var(--tono) 12%, transparent)`,
          } as React.CSSProperties
      }
    >
      {/* La lastra del pezzo elaborato — dossier ed editoriali, vedi
          TIPI_ELABORATI in testa al file.

          Tre segni, tutti e tre a costo zero in altezza, che è il
          vincolo: la riga di un report deve pesare come prima e
          distinguersi lo stesso.

          - il **velo** nel tono, `-mx-3 px-3`: il margine negativo
            allarga la lastra di dodici pixel per lato, il rientro
            restituisce la stessa misura, quindi il testo non si sposta
            di un pixel rispetto a una riga normale e il velo non gli
            tocca le parole. È la forma delle righe di /editoriali.
            **Il dodici per cento è il massimo che si può spendere**, e
            lo decide il tema scuro: il velo schiarisce il fondo, quindi
            tutto quello che ci sta sopra perde contrasto. Misurato sui
            testi di servizio a 12 pixel (data, codice, riferimenti, che
            sono `text-muted`): 5,60:1 sulla pagina nuda, **4,84:1** sul
            velo al 12% e 4,48:1 al 16%, cioè sotto la soglia. Il velo
            da solo sta a 1,2:1 sulla pagina nei due temi — poco, se
            dovesse reggere da solo; ma non deve, perché ad aprirlo c'è
            il filetto qui sotto, che sta a 6,7:1 nel chiaro e 8,7:1
            nello scuro;
          - il **filetto orizzontale** in testa, tre pixel di tono
            pieno, come quello accent sopra la vetrina di /editoriali e
            sopra le lastre di guscio. È `absolute`, quindi non entra
            nel flusso e non allunga la riga;
          - il **marchio**, in apertura della riga meta (più sotto).

          Niente bordo attorno e niente angoli tondi su tutti e quattro
          i lati: una scatola chiusa in mezzo a un elenco di righe è una
          card, e le card in questo elenco erano già state provate e
          tolte. Qui la lastra si apre col filetto e si chiude col
          filetto di separazione che la riga ha già.

          Niente `overflow-hidden`: il pannello dei riferimenti è
          `absolute` dentro la riga e verrebbe tagliato. */}
      <div
        className={
          elaborato
            ? "relative -mx-3 rounded-sm bg-[var(--tono-velo)] px-3 py-5"
            : "py-5"
        }
      >
        {elaborato ? (
          <span
            aria-hidden
            className="absolute inset-x-0 top-0 h-[3px] rounded-t-sm bg-[var(--tono)]"
          />
        ) : null}
        {/* Due colonne da `md`: il **binario dell'ora** e il dispaccio.

            Il binario è la correzione più grossa di questa riga. Prima
            l'ora stava in coda a destra, a 1.072 pixel dal titolo che
            data: un dato che si legge scorrendo, messo dalla parte in
            cui non si scorre. In un rullo — e questo si usa come un
            terminale — si scende lungo il bordo sinistro, e le ore
            devono essere incolonnate una sotto l'altra. Larghezza
            fissa (`4rem`), cifre tabellari, così le colonne combaciano
            anche fra `09:41` e `14:40`.

            **Niente filetto verticale fra le due colonne**, per quanto
            sia il segno che un terminale userebbe: le due strisce
            verticali di questa riga sono già state tolte una volta, e
            cento filetti in colonna tornerebbero a leggersi come un
            bordo dell'elenco. A separare basta il vuoto, che con le
            ore incolonnate è già una verticale.

            Sotto `md` il binario non c'è: a 390 sessantaquattro pixel
            sono un sesto della colonna del testo, e il testo là è già
            la cosa più stretta della pagina. L'ora scende nella riga
            meta (vedi sotto). */}
        <div className="md:grid md:grid-cols-[4rem_minmax(0,1fr)] md:gap-x-5">
          <div
            aria-hidden
            /* `aria-hidden` e non un `<time>`: l'ora c'è già nella riga
               meta per chi legge con la voce (là è `md:hidden`, cioè
               nascosta agli occhi ma non allo screen reader… no, lo è
               anche a lui). Vedi il commento sull'ora della riga meta:
               il valore vero, con il suo `<time datetime>`, sta lì; qui
               è la sua resa incolonnata. */
            className="hidden pt-[1px] text-meta tabular-nums text-muted md:block"
          >
            {binarioRullo(passo, item.time)}
          </div>

          {/* La colonna di lettura, con un tetto.

              Prima il testo correva fino al bordo del sito: a 1440 il
              sommario era largo 1.326 pixel, cioè **circa 130 caratteri
              per riga** (86 a 1024), contro la banda leggibile di
              60-80. Il titolo stava su una riga sola da 89 caratteri.
              Cioè la pagina era meno leggibile sullo schermo grande che
              su quello piccolo, che è il contrario di quello che deve
              succedere.

              Il tetto è in pixel (`42rem`, 672) e non in `ch`: titolo e
              sommario hanno due corpi diversi, 17 e 14, quindi lo
              stesso numero di `ch` darebbe loro due larghezze diverse e
              il blocco uscirebbe frastagliato a destra. A 672 il titolo
              sta a ~68 caratteri e il sommario a ~83: tutti e due nella
              banda, e allineati.

              Il tetto sta su **titolo e sommario**, non su questa
              colonna: la riga meta deve restare larga quanto la riga.
              È la riga di record di una tabella — a sinistra le
              etichette, a destra i dati di servizio — e i suoi due
              estremi devono cadere sulla stessa verticale su tutte e
              cento le righe. Capparla insieme al testo la faceva finire
              a metà pagina, e il vuoto a destra tornava a essere un
              buco invece che una colonna. Con il binario dell'ora a
              sinistra e il codice sempre alla stessa x a destra, quei
              711 pixel di prima non sono più un vuoto: sono la luce fra
              due colonne di una tabella. */}
          <div className="min-w-0">
        {/* La riga meta: dove, di che cosa parla, che forma ha, e tutte
            e tre sono porte verso il rullo filtrato. In coda, i due
            dati di servizio: codice e riferimenti. */}
        <div className="mb-2 flex flex-wrap items-center gap-3">
          {/* Il marchio apre la riga dei pezzi elaborati, come apre
              l'etichetta delle card di /report: è la firma della casa,
              e dice «questo l'abbiamo fatto noi» prima ancora che si
              legga di che tipo è. È il segno «OI», cioè il logo senza
              la scritta, non la fantasia dei cerchi.
              A `text-nota` fa sedici pixel, cioè meno dei diciotto
              della riga meta: entra nell'altezza che c'è già e non la
              alza. Il tono lo prende da `currentColor`.
              Il `-mr-1` non è un ritocco estetico: il marchio più i
              dodici pixel di `gap-3` valgono ventotto, e a 390 la riga
              meta va già a capo da sé — misurati, ventotto in più
              mandavano su una terza riga il dispaccio col paese più
              lungo (riga meta da 48 a 78 pixel, uno su ventuno). Con
              quattro pixel in meno di vuoto le righe elaborate tornano
              a 48 esatti come tutte le altre, a 320, 390, 768, 1024 e
              1440.
              Sui dispacci normali non c'è: un marchio su ogni riga di
              un elenco da cento non firma niente, fa una colonna di
              marchi. */}
          {elaborato ? (
            <SegnoFascia className="-mr-1 text-nota text-[var(--tono)]" />
          ) : null}
          {/* Il paese apre la riga: pallino terracotta + nome, cliccabile
              per filtrare il rullo per quel paese. Prima davanti c'era il
              codice del dispaccio, e la riga cominciava con sette cifre
              da leggere per arrivare all'unica informazione che si
              afferra a colpo d'occhio. Il codice è passato a destra. */}
          {places[0] ? (
            <button
              type="button"
              onClick={() =>
                router.push(`/rullo?place=${encodeURIComponent(places[0]!)}`)
              }
              style={toniVoce("var(--p-place)")}
              // Il filetto prende la terracotta del pallino, il testo
              // no: `--p-place` non si ridichiara in `.dark` (è la
              // stessa in tutti e due i temi, per scelta) e sulla carta
              // scura sta a 2,9:1, che per dodici pixel in grassetto non
              // basta. Sotto il dito il testo va all'accent, che la
              // coppia ce l'ha; il filetto resta un segno, non un testo.
              className={`${VOCE_CLICCABILE} ${VOCE_FILETTO} inline-flex items-center gap-1.5 text-meta font-bold uppercase tracking-[0.04em] text-ink hover:text-accent`}
              title={`Tutti i dispacci da ${places[0]}`}
            >
              <span
                aria-hidden
                className="inline-block h-[8px] w-[8px] flex-shrink-0 rounded-full"
                style={{ backgroundColor: "var(--p-place)" }}
              />
              {places[0]}
            </button>
          ) : null}
          {/* Categoria prima del tipo: "Esteri" dice di che cosa parla
              il dispaccio, "Report" che forma ha. Dopo il paese viene
              naturale sapere l'argomento, non il formato.
              Categoria come testo, non targa: il colore in questa riga
              lo porta già la targa del tipo qui accanto.
              Porta a `?cat=`, che a monte diventa un `$in` sul campo
              `category`, cioè il confronto esatto con la stringa che si
              sta leggendo: va passata com'è, non ripulita. */}
          {rubrica ? (
            <button
              type="button"
              onClick={() =>
                router.push(`/rullo?cat=${encodeURIComponent(rubrica)}`)
              }
              style={toniVoce("var(--color-accent)")}
              className={`${VOCE_CLICCABILE} ${VOCE_FILETTO} inline-flex items-center gap-1.5 text-meta font-semibold uppercase tracking-[0.12em] text-ink/70 hover:text-accent`}
              title={`Tutti i dispacci della rubrica ${rubrica}`}
            >
              {/* Anello vuoto, in coppia col pallino pieno del paese:
                  stessa forma, peso diverso, e si legge come una
                  legenda. Pieno = da dove viene il dispaccio, vuoto = di
                  che cosa parla. Il terzo segno è la targa del tipo, la
                  sola dei tre a portare un colore suo (la tacca e la
                  parola sono nel tono del tipo). */}
              <span
                aria-hidden
                className="inline-block h-[7px] w-[7px] flex-shrink-0 rounded-full border border-accent/70"
              />
              {rubrica}
            </button>
          ) : null}
          {/* Il tipo derivato dalla cronologia (notizia, caso, report),
              terza voce dopo paese e rubrica: dice quanta storia c'è
              dietro il dispaccio, mentre la targa qui accanto dice il
              `type` di Strapi. Testo e non bottone, perché a monte non c'è
              un campo su cui filtrarlo; stesso corpo e stessa spaziatura
              della rubrica, senza il segno davanti. Assente se il
              conteggio non c'è. */}
          {item.tipoDerivato ? (
            <span className="whitespace-nowrap text-meta font-semibold uppercase tracking-[0.12em] text-muted">
              {TIPO_DERIVATO[item.tipoDerivato].label}
            </span>
          ) : null}
          {/* Il tipo porta al rullo filtrato per tipo. La targa resta
              quella di sempre: il bottone le sta intorno e non le tocca
              né il corpo né il colore, così la stessa parola si legge
              uguale qui, in prima pagina e sulla pagina del dispaccio.
              Il tono del filetto è `--tono`, che la riga dichiara già
              sull'`<article>`: è il tono del tipo, dichiarato in tutti
              e due i temi. */}
          <button
            type="button"
            onClick={() => router.push(`/rullo?priority=${item.priority}`)}
            style={toniVoce("var(--tono)")}
            className={`${VOCE_CLICCABILE} ${VOCE_FILETTO} inline-flex`}
            title={`Tutti i dispacci di tipo ${PRIORITY_MAP[item.priority].label}`}
          >
            <PriorityPill priority={item.priority} variant="kicker" />
          </button>
          {/* Coda destra: codice e riferimenti. Il conteggio chiude la
              riga al margine: è l'unico dei due che si clicca, e in
              mezzo ai dati sembrava un dato anche lui.
              `sm:ml-auto` spinge il gruppo a destra; sotto sm resta in
              fila con gli altri e va a capo se serve. */}
          <span className="inline-flex items-center gap-3 sm:ml-auto">
            {/* L'ora, **solo sotto `md`**: da lì in su sta nel binario
                a sinistra. Il valore è lo stesso, la resa no, e la
                copia costa una chiamata a una funzione pura: spostare
                un nodo da una colonna all'altra col solo CSS non si fa
                in modo affidabile, e duplicare il componente dei
                riferimenti (che ha uno stato e un pannello) sarebbe
                stato ben peggio.
                `<time>` col `dateTime` completo: la data per esteso non
                si stampa più in riga — la dice lo stacco di giornata —
                ma una macchina deve poterla leggere lo stesso. */}
            <time
              dateTime={new Date(item.time).toISOString()}
              className="text-meta tabular-nums text-muted md:hidden"
            >
              {binarioRullo(passo, item.time)}
            </time>

            {/* Il codice del dispaccio, smorzato.
                Serve a citarlo, non a leggerlo, e stava a 12 pixel in
                semibold con la spaziatura larga, cioè con lo stesso
                peso dell'ora, ripetuto su cento righe. Adesso è a 10 e
                senza grassetto: chi lo cerca lo trova, chi legge non
                ci inciampa. */}
            <span className="text-nota tabular-nums tracking-dato text-muted">
              {fmtDispatchId(item.id)}
            </span>

            {refCount > 0 ? (
              /* L'ancora del pannello: `relative` perché il pannello si
                 appende qui sotto, allineato al bordo destro del
                 comando. Il riferimento serve alla chiusura al tocco
                 fuori: tutto quello che sta dentro questa scatola —
                 comando e pannello — non è "fuori". */
              <span ref={ancora} className="relative inline-flex items-center">
              <button
                type="button"
                onClick={commuta}
                aria-expanded={refsOpen}
                title={refsOpen ? "Nascondi i riferimenti" : "Mostra i riferimenti"}
                aria-label={
                  refsOpen
                    ? "Nascondi i riferimenti del dispaccio"
                    : `Mostra i ${refCount} riferimenti del dispaccio`
                }
                /* Aperto, il comando resta acceso: è l'unico segno che
                   la riga porta lo stato, e sta dentro una scatola che
                   ha già le sue misure — non è una barra appesa nel
                   margine. */
                className={`group/ref inline-flex cursor-pointer items-center gap-1.5 bg-transparent p-0 text-nota uppercase tracking-dato tabular-nums transition-colors hover:text-accent ${
                  refsOpen ? "text-accent" : "text-muted"
                }`}
              >
                {/* L'etichetta dice che cosa c'è dietro, con la stessa
                    parola che intesta la scheda che apre ("Riferimenti
                    nel dispaccio"). Prima era "◈ 12": il numero non
                    diceva di che cosa, e il rombo non lo spiegava,
                    perché è un glifo che non nomina niente. Il marchio,
                    che nel sito apre le fasce, qui non va: sarebbe un
                    secondo segno a tre millimetri da quello in testa
                    alla scheda.
                    Non è il ritorno della vecchia "Apri", che era un
                    comando e aveva il tono di un'applicazione: il
                    comando resta il quadratino qui sotto, e la parola è
                    un dato, come il codice e l'ora che le stanno
                    accanto. */}
                <span className="whitespace-nowrap">
                  {refCount} {refCount === 1 ? "riferimento" : "riferimenti"}
                </span>
                {/* Il segno di espansione dei terminali finanziari e
                    delle tabelle: un più dentro un quadratino a filo
                    sottile, che diventa un meno quando è aperto. Non ha
                    bisogno di essere spiegato e non alza la voce. Prima
                    qui c'era una freccetta da otto pixel, che si
                    leggeva come punteggiatura.
                    Il filo è `border-accent` pieno, e non più
                    `border-accent/40`: misurato con
                    /tmp/pwmeas/rullo-contrasti.js sui due fondi su cui
                    la riga lo mostra, il quaranta per cento stava a
                    2,09:1 sul fondo pagina e 2:1 sopra il velo in tema
                    chiaro, 1,76:1 e 1,78:1 nello scuro, cioè sotto i
                    3:1 che un segno non testuale deve avere; a filo
                    pieno sono 8,95/7,47 e 5,26/4,44. Provate in pagina
                    anche la mezza via a /70 (2,87:1 sul velo nello
                    scuro, ancora sotto) e un quadratino che segue il
                    tono del testo accanto, scartato perché nello scuro
                    il riposo sarebbe uscito più marcato dell'acceso.
                    Acceso, il quadratino si riempie di accento pieno
                    con il glifo in `onAccent`. Era `bg-accentSoft`, che
                    sopra il velo vale 1,02:1 nel chiaro e 1,06:1 nello
                    scuro: lo stato aperto, cioè la sola cosa che
                    distingue questa riga dalle altre novantanove, non
                    si vedeva. Riposo e acceso restano distinti perché
                    uno è un contorno e l'altro è un pieno, non perché
                    uno è più pallido. */}
                <span
                  aria-hidden
                  className={`inline-flex h-4 w-4 items-center justify-center rounded-[2px] border border-accent text-nota leading-none transition-colors group-hover/ref:bg-accent group-hover/ref:text-onAccent ${
                    refsOpen ? "bg-accent text-onAccent" : "text-accent"
                  }`}
                >
                  {refsOpen ? "−" : "+"}
                </span>
              </button>

              {refsOpen ? (
                <RefPanel
                  groups={refGroups}
                  total={refCount}
                  verso={verso}
                  /* Prima il push, poi la chiusura: smontare nello
                     stesso giro dell'evento l'elemento che è stato
                     toccato è il modo noto per perdere una navigazione
                     (era successo col pannello di ricerca, che è un
                     portale). Qui la lista si rifà comunque da sé. */
                  onPick={(href) => {
                    router.push(href);
                    setRefsOpen(false);
                  }}
                  onClose={() => setRefsOpen(false)}
                />
              ) : null}
              </span>
            ) : null}
          </span>
        </div>

        {/* Il titolo è la notizia. 17,5px: partiva da 21, che staccava
            troppo dal lead a 14,5 e sbilanciava la riga in alto. A
            reggere l'evidenza è il peso, 800, che con Geist (variabile
            100–900) è un grassetto vero e non un finto neretto
            ingrossato dal browser. Meno corpo e più nero: si vede
            uguale e ingombra meno. Interlinea 1,3, che a corpo piccolo
            serve per non incollare le righe di un titolo lungo. */}
        <h3 className="oi-titolo m-0 mb-2 max-w-[42rem] text-titolo font-extrabold leading-[1.3] tracking-[-0.015em] text-ink">
          {/* L'unico punto della riga che porta all'articolo per chi
              usa la tastiera o un lettore di schermo. Il bersaglio è il
              testo del titolo e basta: `inline` e non `block`, così non
              si allarga a tutta la colonna prendendosi anche lo spazio
              vuoto a destra dell'ultima parola.
              Il contorno del fuoco da tastiera qui torna: prima era
              disattivato perché il link copriva l'intera riga e il
              rettangolo sembrava una card, ora circonda il solo titolo
              ed è esattamente quello che serve. */}
          <Link
            href={articleHref(item)}
            className="text-ink transition-colors hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {splitByQuery(item.title, highlight ?? "").map((seg, i) =>
              seg.hit ? (
                <mark
                  key={i}
                  // Fondo giallo, non più una sottolineatura: chi cerca
                  // una parola vuole vederla saltare fuori dalla riga.
                  className="rounded-[2px] px-[1px] text-inherit"
                  style={{
                    backgroundColor: "var(--mark-bg)",
                  }}
                >
                  {seg.text}
                </mark>
              ) : (
                <Fragment key={i}>{seg.text}</Fragment>
              )
            )}
          </Link>
        </h3>
        {/* Il sommario si rende sempre: i riferimenti non gli tolgono
            più il posto e non si mettono più sotto di lui. Stanno in un
            pannello ancorato al comando che li apre, in fondo alla riga
            meta qui sopra, e sono l'unica cosa di questa riga che non
            vive nel flusso della pagina. Il perché è in testa a
            <RefPanel>. */}
        {/* **Niente filetto verticale davanti al sommario.** C'è stato
            un `border-l-2` neutro con dodici pixel di rientro, eredità
            di quando il sommario si voleva far leggere come una
            citazione. Era la seconda riga verticale della riga — la
            prima era la striscia del tono sul fianco — e il rientro
            spostava il sommario rispetto al titolo e alla riga meta,
            che attaccano al margine: tre blocchi dello stesso dispaccio
            su due verticali diverse. Tolto il filetto va via anche il
            rientro, e i tre blocchi si incolonnano. */}
        {/* Tre righe al massimo sul telefono. A 390 il sommario è di
            420 caratteri su una colonna da 37 caratteri per riga, cioè
            **sette righe**: da solo vale metà dei 333 pixel della riga
            e la pagina arriva a 32.164 pixel per cento dispacci, che
            sono trentotto schermate. Tre righe dicono di che cosa
            parla, che è quello che serve per decidere se aprirlo; il
            resto è nel dispaccio.

            Il tetto vale **a ogni larghezza**, e non costa niente sul
            sito: prima il sommario stava in due righe da 130 caratteri,
            cioè 260 visibili; adesso sta in tre righe da 83, cioè 249.
            Praticamente lo stesso testo, leggibile invece che no. */}
        <p className="oi-sommario m-0 line-clamp-3 max-w-[42rem] text-corpo font-medium leading-[1.65] text-ink/75">
          {splitByQuery(short, highlight ?? "").map((seg, i) =>
            seg.hit ? (
              <mark
                key={i}
                className="rounded-[2px] px-[1px] text-inherit"
                style={{
                  backgroundColor: "var(--mark-bg)",
                }}
              >
                {seg.text}
              </mark>
            ) : (
              <Fragment key={i}>{seg.text}</Fragment>
            )
          )}
        </p>
          </div>
        </div>
      </div>
    </article>
  );
}
